import { Key, Rect } from "client/Misc/commonTypes";
import {
	ensureNotNil,
	isNil,
	isNotNil
} from "client/Misc/utils";
import { outOfContextCall } from "client/Misc/reactUtils";
import { tryResolveKeyboardKey } from "client/Misc/keyboardUtils";
import { findLastIndex } from "client_legacy/ndaw/arrayHelpers";
import {
	createContext,
	KeyboardEvent,
	ReadonlyRefObject,
	useCallback,
	useContext,
	useMemo,
	useRef
} from "react";
import { useValueEqualMemo } from "./useValueEqualMemo";
import { never } from "client_legacy/ndaw/errors";

export type GridNavigationDirection =
	| Key.ArrowUp
	| Key.ArrowDown
	| Key.ArrowLeft
	| Key.ArrowRight;

function isGridNavigationDirection(key: Key): key is GridNavigationDirection {
	return [
		Key.ArrowUp,
		Key.ArrowDown,
		Key.ArrowLeft,
		Key.ArrowRight
	].includes(key);
}

export type KeyboardGridNavigationContextData = {
	focus: (direction: GridNavigationDirection, index: number) => void;
	setElement: (index: number, element: HTMLElement | null) => void;
};

export const KeyboardGridNavigationContext = createContext<KeyboardGridNavigationContextData>({
	focus: outOfContextCall("KeyboardGridNavigationContext", "focus"),
	setElement: outOfContextCall("KeyboardGridNavigationContext", "setElement")
});

export type KeyboardGridNavigationOptions = {
	onOutOfBounds?: (
		direction: GridNavigationDirection,
		rect: Rect
	) => void;
	onFocusChange?: (index: number) => void;
};

export type KeyboardGridNavigationHandlers = {
	contextData: KeyboardGridNavigationContextData;
	focusItemAtHandler: (target: FocusItemAtTarget) => void;
	totalItems: number;
};

export function useKeyboardGridNavigation(
	options: KeyboardGridNavigationOptions
): KeyboardGridNavigationHandlers {
	const {
		onOutOfBounds,
		onFocusChange
	} = options;

	const elementRefs: ReadonlyRefObject<(HTMLElement | null)[]> = useRef<(HTMLElement | null)[]>([]);

	const focusHandler = useCallback(
		(direction: GridNavigationDirection, index: number): void => {
			const { current: refs } = elementRefs;

			const sourceElement: HTMLElement = ensureNotNil(refs[index]);
			const sourceElementRect: Rect = sourceElement.getBoundingClientRect();

			const targetIndexSearch: TryResult<number> = tryFindTarget(
				direction,
				index,
				sourceElementRect,
				refs
			);

			if (!targetIndexSearch.wasSuccessful) {
				onOutOfBounds?.(
					direction,
					sourceElementRect
				);

				return;
			}

			const targetIndex: number = targetIndexSearch.result;

			refs[targetIndex]?.focus();
			onFocusChange?.(targetIndex);
		},
		[
			onOutOfBounds,
			onFocusChange
		]
	);

	const contextData = useMemo(
		(): KeyboardGridNavigationContextData => {
			return {
				focus: focusHandler,
				setElement: (index: number, element: HTMLElement | null): void => {
					elementRefs.current[index] = element;
					const indexOfLastSetRef: number = findLastIndex(
						elementRefs.current,
						isNotNil
					);

					elementRefs.current.length = indexOfLastSetRef + 1;
				},
			};
		},
		[focusHandler]
	);

	const focusItemAtHandler = useCallback(
		(target: FocusItemAtTarget): void => {
			let targetIndexSearch: TryResult<number>;

			if (target === "start") {
				const potentialTargetIndex: number = elementRefs.current
					.findIndex(isNotNil);

				targetIndexSearch = potentialTargetIndex === -1
					? { wasSuccessful: false }
					: { wasSuccessful: true, result: potentialTargetIndex };
			} else if (target === "end") {
				const potentialTargetIndex: number = findLastIndex(elementRefs.current, isNotNil);

				targetIndexSearch = potentialTargetIndex === -1
					? { wasSuccessful: false }
					: { wasSuccessful: true, result: potentialTargetIndex };
			} else {
				targetIndexSearch = tryFindTarget(
					target.direction,
					target.direction === Key.ArrowUp
						|| target.direction === Key.ArrowLeft
						? "outside-after"
						: target.direction === Key.ArrowDown
							|| target.direction === Key.ArrowRight
							? "outside-before"
							: never(target.direction),
					target.rect,
					elementRefs.current
				);

				if (!targetIndexSearch.wasSuccessful) {
					onOutOfBounds?.(target.direction, target.rect);
					return;
				}
			}

			if (targetIndexSearch.wasSuccessful) {
				const targetIndex: number = targetIndexSearch.result;
				const element: HTMLElement = ensureNotNil(
					elementRefs.current[
						targetIndex
					]
				);

				element.focus();
				onFocusChange?.(targetIndex);
			}
		},
		[onFocusChange, onOutOfBounds]
	);

	return useMemo(
		(): KeyboardGridNavigationHandlers => ({
			contextData,
			focusItemAtHandler,
			totalItems: elementRefs.current
				.length
		}),
		[contextData, focusItemAtHandler]
	);
}

export type FocusItemAtTarget =
	| "start"
	| "end"
	| {
		direction: GridNavigationDirection;
		rect: Rect;
	};

export type KeyboardGridNavigationItemHandlers = {
	ref: (element: HTMLElement | null) => void;
	onKeyDown: (args: KeyboardEvent<HTMLElement>) => boolean;
};

export type KeyboardGridNavigationItemOptions = {
	index: number;
	onExecute?: (key: Key) => void;
	executeKey?: Key | (readonly Key[]);
};

export function useKeyboardGridNavigationItem(
	options: KeyboardGridNavigationItemOptions
): KeyboardGridNavigationItemHandlers {
	const {
		setElement,
		focus
	} = useContext(KeyboardGridNavigationContext);

	const {
		index,
		onExecute,
		executeKey = Key.Enter
	} = options;

	const executeKeys = useValueEqualMemo(
		(): ReadonlySet<Key> => {
			if (typeof executeKey === "string") {
				return new Set([executeKey]);
			}
			return new Set(executeKey);
		},
		[executeKey]
	);

	return useMemo(
		(): KeyboardGridNavigationItemHandlers => ({
			ref: (element: HTMLElement | null): void => {
				setElement(index, element);
			},
			onKeyDown: (args: KeyboardEvent): boolean => {
				const key: Key | null = tryResolveKeyboardKey(args);

				if (!key) {
					return false;
				}

				if (executeKeys.has(key) && isNotNil(onExecute)) {
					args.preventDefault();
					onExecute(key);
					return true;
				}

				if (isGridNavigationDirection(key)) {
					focus(key, index);
					args.preventDefault();
					return true;
				}

				return false;
			},
		}),
		[
			focus,
			index,
			onExecute,
			setElement,
			executeKeys
		]
	);
}

function tryFindTarget(
	direction: GridNavigationDirection,
	currentPositionIndex: number | "outside-before" | "outside-after",
	sourceElementRect: Rect,
	elements: readonly (HTMLElement | null)[]
): TryResult<number> {
	const lastIndex: number = findLastIndex(elements, isNotNil);

	const resolvedCurrentPositionIndex: number = currentPositionIndex === "outside-before"
		? -1
		: currentPositionIndex === "outside-after"
			? lastIndex + 1
			: currentPositionIndex;

	switch (direction) {
		case Key.ArrowUp: {
			for (let targetIndex = resolvedCurrentPositionIndex - 1; targetIndex >= 0; targetIndex--) {
				const targetElement: HTMLElement | null = elements[targetIndex];

				if (isNil(targetElement)) {
					continue;
				}

				const targetElementRect: Rect = targetElement.getBoundingClientRect();

				if (areAlignedHorizontally(sourceElementRect, targetElementRect)) {
					return {
						wasSuccessful: true,
						result: targetIndex
					};
				}
			}

			return { wasSuccessful: false };
		}
		case Key.ArrowDown: {
			for (let targetIndex = resolvedCurrentPositionIndex + 1; targetIndex <= lastIndex; targetIndex++) {
				const targetElement: HTMLElement | null = elements[targetIndex];

				if (isNil(targetElement)) {
					continue;
				}

				const targetElementRect: Rect = targetElement.getBoundingClientRect();

				if (areAlignedHorizontally(sourceElementRect, targetElementRect)) {
					return { wasSuccessful: true, result: targetIndex };
				}
			}

			return { wasSuccessful: false };
		}
		case Key.ArrowLeft: {
			for (let targetIndex = resolvedCurrentPositionIndex - 1; targetIndex >= 0; targetIndex--) {
				if (!isNil(elements[targetIndex])) {
					return { wasSuccessful: true, result: targetIndex };
				}
			}

			return { wasSuccessful: false };
		}
		case Key.ArrowRight: {
			for (let targetIndex = resolvedCurrentPositionIndex + 1; targetIndex <= lastIndex; targetIndex++) {
				if (!isNil(elements[targetIndex])) {
					return { wasSuccessful: true, result: targetIndex };
				}
			}

			return { wasSuccessful: false };
		}
		default:
			never(direction);
	}
}

function areAlignedHorizontally(sourceElementRect: Rect, targetElementRect: Rect): boolean {
	return sourceElementRect.left >= targetElementRect.left
		&& sourceElementRect.left < targetElementRect.right;
}